import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  fuelApi,
  type ConfirmSelfAssignmentDto,
  type FuelArchiveResponse,
  type FuelReceiptEvidence,
  type FuelResponse,
  type FuelSelfAssignmentConfirmation,
  type SaveFuelDto,
  type SaveReceiptEvidenceDto,
} from '@/api/fuel';

export function useFuel() {
  const queryClient = useQueryClient();

  const query = useQuery<FuelResponse>({
    queryKey: ['fuel'],
    queryFn: fuelApi.get,
    staleTime: 60_000, // 1 minute
  });

  const saveMutation = useMutation({
    mutationFn: (dto: SaveFuelDto) => fuelApi.save(dto),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['fuel'] });
      toast.success(result.action === 'created' ? 'Заправка добавлена' : 'Заправка обновлена');
    },
    onError: () => {
      toast.error('Ошибка сохранения');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => fuelApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fuel'] });
      toast.success('Заправка удалена');
    },
    onError: () => {
      toast.error('Ошибка удаления');
    },
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    isRefetching: query.isRefetching,
    save: saveMutation.mutate,
    isSaving: saveMutation.isPending,
    delete: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
  };
}

export function useFuelArchive(enabled: boolean) {
  return useQuery<FuelArchiveResponse>({
    queryKey: ['fuel-archive'],
    queryFn: fuelApi.getArchive,
    enabled,
    staleTime: 60_000,
  });
}

export function useFuelReceiptEvidence(month?: string) {
  const queryClient = useQueryClient();

  const query = useQuery<{ items: FuelReceiptEvidence[]; meta: { total: number } }>({
    queryKey: ['fuel-receipt-evidence', month],
    queryFn: () => fuelApi.listReceiptEvidence({ month }),
    staleTime: 60_000,
  });

  const saveMutation = useMutation({
    mutationFn: ({ dto, photo }: { dto: SaveReceiptEvidenceDto; photo?: File | Blob }) =>
      fuelApi.saveReceiptEvidence(dto, photo),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fuel-receipt-evidence'] });
      queryClient.invalidateQueries({ queryKey: ['fuel'] });
      toast.success('Чек сохранен');
    },
    onError: () => {
      toast.error('Ошибка сохранения чека');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => fuelApi.deleteReceiptEvidence(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fuel-receipt-evidence'] });
      queryClient.invalidateQueries({ queryKey: ['fuel'] });
      toast.success('Чек удален');
    },
    onError: () => {
      toast.error('Ошибка удаления чека');
    },
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    isRefetching: query.isRefetching,
    save: saveMutation.mutate,
    isSaving: saveMutation.isPending,
    delete: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
  };
}

export function useFuelAssignmentConfirmation(month?: string) {
  const queryClient = useQueryClient();

  const query = useQuery<FuelSelfAssignmentConfirmation>({
    queryKey: ['fuel-assignment-confirmation', month],
    queryFn: () => fuelApi.getSelfAssignmentConfirmation({ month }),
    enabled: Boolean(month),
    staleTime: 60_000,
  });

  const confirmMutation = useMutation({
    mutationFn: (dto: ConfirmSelfAssignmentDto) => fuelApi.confirmSelfAssignment(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fuel-assignment-confirmation'] });
      toast.success('Машина и карта подтверждены');
    },
    onError: () => {
      toast.error('Ошибка подтверждения машины и карты');
    },
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    isRefetching: query.isRefetching,
    confirm: confirmMutation.mutate,
    isConfirming: confirmMutation.isPending,
  };
}
